<script lang="ts" setup>
import { AppTypography, AppTextField, AppDatePicker, AppButton, AppFileUpload, AppSelectField } from '@/components/base';
import type { AvailableTrainingQualification } from '@/modules/auth/types/AvailableTrainingQualification';
import type { TrainingQualification } from '@/modules/auth/types/TrainingQualification';
import type { WWCCQualificationType } from '@/modules/auth/types/WWCCQualificationType';
import { DESCRIPTION_ONLY, FILE_ONLY } from '@/modules/auth/utils/enums/TrainingQualificationRules';
import { helpers, requiredIf } from '@vuelidate/validators';

// WWCC Qualification Type Configuration
const WWCC_CONFIG = {
  'WWCC_NSW': {
    fields: [
      { name: 'nsw_wwcc_last_name', label: 'Last Name', type: 'text', required: true },
      { name: 'nsw_wwcc_dob', label: 'DOB', type: 'date', required: true },
      { 
        name: 'nsw_wwcc_reference_number', 
        label: 'Reference Number', 
        type: 'text', 
        required: true, 
        validation: '^WWC\\d{7}[A-Z]$',
        example: 'WWC1234567A',
        errorMessage: 'Reference Number must start with WWC, followed by 7 digits, and end with a letter (e.g., WWC1234567A)'
      }
    ]
  },
  'BLUE_CARD_QLD': {
    fields: [
      { name: 'qld_blue_card_full_name', label: 'Full Name', type: 'text', required: true },
      { name: 'qld_blue_card_expiry_date', label: 'Card Expiry Date', type: 'date', required: true },
      { 
        name: 'qld_blue_card_number', 
        label: 'Card Number', 
        type: 'text', 
        required: true, 
        validation: '^\\d{7}/\\d$',
        example: '1234567/1',
        errorMessage: 'Card Number must be 7 digits followed by a slash and 1 digit (e.g., 1234567/1)'
      }
    ]
  },
  'BLUE_CARD_EXEMPTION_QLD': {
    fields: [
      { name: 'qld_blue_card_exemption_full_name', label: 'Full Name', type: 'text', required: true },
      { name: 'qld_blue_card_exemption_expiry_date', label: 'Card Expiry Date', type: 'date', required: true },
      { 
        name: 'qld_blue_card_exemption_number', 
        label: 'Card Number', 
        type: 'text', 
        required: true, 
        validation: '^\\d{7}/\\d$',
        example: '1234567/1',
        errorMessage: 'Card Number must be 7 digits followed by a slash and 1 digit (e.g., 1234567/1)'
      }
    ]
  },
  'WWVP_ACT': {
    fields: [
      { name: 'act_wwvp_full_name', label: 'Full Name', type: 'text', required: true },
      { name: 'act_wwvp_dob', label: 'DOB', type: 'date', required: true },
      { 
        name: 'act_wwvp_registration_number', 
        label: 'WWVP registration number', 
        type: 'text', 
        required: true, 
        validation: '^\\d{5,6}$',
        example: '123456',
        errorMessage: 'WWVP registration number must be 5 to 6 digits (e.g., 123456)'
      }
    ]
  },
  'WWCC_NT': {
    fields: [
      { name: 'nt_wwc_full_name', label: 'Full Name', type: 'text', required: true },
      { name: 'nt_wwc_dob', label: 'DOB', type: 'date', required: true },
      { 
        name: 'nt_wwc_clearance_notice_no', 
        label: 'Clearance Notice No', 
        type: 'text', 
        required: true, 
        validation: '^\\d{1,6}$',
        example: '123456',
        errorMessage: 'Clearance Notice No must be 1 to 6 digits (e.g., 123456)'
      }
    ]
  },
  'WWCC_SA': {
    fields: [
      { name: 'sa_wwcc_full_name', label: 'Full Name', type: 'text', required: true },
      { 
        name: 'sa_wwcc_unique_id', 
        label: 'Unique ID', 
        type: 'text', 
        required: true, 
        validation: '^SRN \\d{4}-\\d{4}$',
        example: 'SRN 1234-5678',
        errorMessage: 'Unique ID must be in format SRN xxxx-xxxx (e.g., SRN 1234-5678)'
      }
    ]
  },
  'WWCC_VIC': {
    fields: [
      { name: 'vic_wwcc_last_name', label: 'Last Name', type: 'text', required: true },
      { 
        name: 'vic_wwcc_card_or_application_number', 
        label: 'Card or Application Number', 
        type: 'text', 
        required: true, 
        validation: '^\\d{7}[A-Z]$',
        example: '1234567A',
        errorMessage: 'Card or Application Number must be 7 digits followed by 1 uppercase letter (e.g., 1234567A)'
      }
    ]
  },
  'WWCC_WA': {
    fields: [
      { name: 'wa_wwcc_last_name', label: 'Last Name', type: 'text', required: true },
      { 
        name: 'wa_wwcc_notice_or_card_number', 
        label: 'Notice/Card Number', 
        type: 'text', 
        required: true, 
        validation: '^\\d{5,7}$',
        example: '123456',
        errorMessage: 'Notice/Card Number must be 5 to 7 digits (e.g., 123456)'
      }
    ]
  },
  'WWCC_TAS': {
    fields: [
      { name: 'tas_wwc_last_name', label: 'Last Name', type: 'text', required: true },
      { 
        name: 'tas_wwc_application_card_ndis_id', 
        label: 'Application/Card/ NDIS Worker Screening ID', 
        type: 'text', 
        required: true, 
        validation: '^\\d{8}$',
        example: '12345678',
        errorMessage: 'Application/Card/ NDIS Worker Screening ID must be exactly 8 digits (e.g., 12345678)'
      }
    ]
  }
};

const props = withDefaults(
  defineProps<{
    rule: AvailableTrainingQualification;
    form: TrainingQualification;
    disabled?: boolean;
    errors?: {
      [key: string]: string | string[];
    };
  }>(),
  {
    disabled: false,
    errors: () => ({}),
  }
);

const { VALIDATIONS } = useValidation();
const { wwccQualificationTypes, fetching: fetchingWWCCTypes, getWWCCQualificationTypes } = useWWCCQualificationTypes();

const isWWCCRule = computed(() => {
  return props.rule.name === 'WWCC / WWVP / Blue Card';
});

const shouldShowWWCCDropdown = computed(() => {
  return props.form.enabled && isWWCCRule.value;
});

const selectedWWCCConfig = computed(() => {
  if (!props.form.qualification_type) return null;
  
  // Try to find the config by exact match first
  let config = WWCC_CONFIG[props.form.qualification_type as keyof typeof WWCC_CONFIG];
  
  // If not found, try to find by partial match
  if (!config) {
    const selectedType = props.form.qualification_type;
    const matchingKey = Object.keys(WWCC_CONFIG).find(key => 
      selectedType.includes(key) || key.includes(selectedType)
    );
    if (matchingKey) {
      config = WWCC_CONFIG[matchingKey as keyof typeof WWCC_CONFIG];
    }
  }
  
  return config;
});

const isEnabled = computed((): boolean => {
  return props.form.enabled || false;
});

const validate = async (): Promise<boolean> => {
  if (!props.form.enabled) {
    return true;
  }
  
  // Simple validation - just check if required fields are filled
  if (shouldShowWWCCDropdown.value && selectedWWCCConfig.value) {
    for (const field of selectedWWCCConfig.value.fields) {
      if (field.required) {
        const fieldValue = (props.form as any)[field.name];
        if (!fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined) {
          return false;
        }
        
        // Special validation for date fields (DOB)
        if (field.name.includes('dob') && fieldValue) {
          // Check date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(fieldValue)) {
            return false;
          }
          
          // Check if date is valid
          const date = new Date(fieldValue);
          if (isNaN(date.getTime())) {
            return false;
          }
          
          // Check if date is not in the future
          const today = new Date();
          if (date > today) {
            return false;
          }
          
          // Check if person is at least 18 years old
          const age = today.getFullYear() - date.getFullYear();
          const monthDiff = today.getMonth() - date.getMonth();
          
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
            // Birthday hasn't occurred yet this year
            if (age - 1 < 18) {
              return false;
            }
          } else {
            // Birthday has occurred this year
            if (age < 18) {
              return false;
            }
          }
        }
      }
    }
  }
  
  return true;
};

const removeAttachment = (): void => {
  props.form.attachment = '';
};

// Fetch WWCC qualification types when component is mounted and condition is met
onMounted(async () => {
  if (shouldShowWWCCDropdown.value) {
    await getWWCCQualificationTypes();
  }
});

// Watch for changes in the condition and fetch data when needed
watch(shouldShowWWCCDropdown, async (newValue) => {
  if (newValue && wwccQualificationTypes.value.length === 0) {
    await getWWCCQualificationTypes();
  }
});

defineExpose({
  validate,
});
</script>
<template>
  <v-card v-if="rule" flat class="qualification-form">
    <v-card-title class="d-flex justify-space-between px-0">
      <AppTypography class="px-4 py-5" element="span" type="headline-1">{{ rule.name }}</AppTypography>
      <v-checkbox v-model="form.enabled" :disabled="disabled" color="primary" hide-details data-testid="enable">
      </v-checkbox>
    </v-card-title>
    <v-card-text v-if="isEnabled" class="qualification-form__fields pb-0">
      <v-row class="qualification-form__form mb-5">
        <v-col cols="12" v-if="!shouldShowWWCCDropdown">
          <AppTypography type="subheadline-1" class="mb-2">Issue Date</AppTypography>
          <AppDatePicker
            v-model="form.issued_at"
            :error-messages="errors.issued_at"
            :disabled="disabled"
            placeholder="DD/MM/YYYY"
            hide-details="auto"
            append-icon=""
            label=""
            data-testid="issue-date"
            :allow-typing="false"
            :allow-selecting-dates="true"
          />
        </v-col>
        <v-col v-if="shouldShowWWCCDropdown" cols="12">
          <AppTypography type="subheadline-1" class="mb-2">WWCC Qualification Type</AppTypography>
          <AppSelectField
            v-model="form.qualification_type"
            :items="wwccQualificationTypes"
            item-value="name"
            item-title="value"
            :error-messages="errors.qualification_type"
            :disabled="disabled || fetchingWWCCTypes"
            placeholder="Select WWCC qualification type"
            hide-details="auto"
            data-testid="wwcc-qualification-type"
          />
        </v-col>
        
        <!-- Dynamic WWCC Fields -->
        <template v-if="selectedWWCCConfig">
          <v-col v-for="field in selectedWWCCConfig.fields" :key="field.name" cols="12">
            <AppTypography type="subheadline-1" class="mb-2">{{ field.label }}</AppTypography>
            <AppDatePicker
              v-if="field.type === 'date'"
              v-model="form[field.name]"
              :error-messages="props.errors?.[field.name]"
              :disabled="disabled"
              placeholder="YYYY-MM-DD"
              hide-details="auto"
              append-icon=""
              label=""
              :data-testid="`wwcc-${field.name}`"
              format="YYYY-MM-DD"
              :allow-typing="false"
              :allow-selecting-dates="true"
            />
            <AppTextField
              v-else
              v-model="form[field.name]"
              :placeholder="field.label"
              :error-messages="props.errors?.[field.name]"
              :disabled="disabled"
              hide-details="auto"
              :data-testid="`wwcc-${field.name}`"
            />
          </v-col>
        </template>
        <v-col v-if="rule.requirements === FILE_ONLY && !shouldShowWWCCDropdown" cols="12">
          <AppTypography type="subheadline-1" class="mb-2">Upload File</AppTypography>
          <AppFileUpload
            v-model="form.media"
            :value="form.attachment"
            :error-messages="props.errors?.media"
            :disabled="disabled"
            hide-details="auto"
            data-testid="file"
            @removed="removeAttachment()"
          />
        </v-col>
        <v-col v-if="rule.requirements === DESCRIPTION_ONLY" cols="12">
          <AppTypography type="subheadline-1" class="mb-2">Description</AppTypography>
          <AppTextField
            v-model="form.description"
            placeholder="Description"
            :error-messages="errors.description"
            :disabled="disabled"
            hide-details="auto"
            data-testid="description"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<style lang="scss" scoped>
.qualification-form {
  border-radius: 16px;
}
</style>


